import { useState } from "react";
import { Link } from "wouter";
import Footer from "../components/Footer";
import { trpc } from "../lib/trpc";
import Header from "../components/Header";
import StoreCard from "../components/StoreCard";

const AREAS = [
  "渋谷", "新宿", "池袋", "銀座", "表参道", "恵比寿", "中目黒", "自由が丘", "吉祥寺", "二子玉川", "その他",
] as const;

export default function StoresPage() {
  const [area, setArea] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    strollerEntry: undefined as boolean | undefined,
    hasDiaperChanging: undefined as boolean | undefined,
    hasNursingRoom: undefined as boolean | undefined,
    hasKidsMenu: undefined as boolean | undefined,
  });
  const [page, setPage] = useState(1);

  const { data, isLoading } = trpc.stores.list.useQuery({
    area: area as any,
    page,
    limit: 12,
    search: search || undefined,
    ...filters,
  });

  const toggleFilter = (key: keyof typeof filters) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key] === true ? undefined : true,
    }));
    setPage(1);
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length + (area ? 1 : 0);

  const clearAllFilters = () => {
    setSearch("");
    setArea(undefined);
    setFilters({
      strollerEntry: undefined,
      hasDiaperChanging: undefined,
      hasNursingRoom: undefined,
      hasKidsMenu: undefined,
    });
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      <main className="pt-24 pb-12">
        <div className="container">
          {/* Search Section */}
          <div className="mb-8">
            {/* Search Bar */}
            <div className="flex gap-3 mb-4">
              <div className="flex-1 relative">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="店舗名、エリア、住所で検索..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-12 h-12 text-base rounded-xl border border-stone-200 bg-stone-100/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all outline-none"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`h-12 px-4 rounded-xl gap-2 flex items-center font-medium transition-colors ${
                  showFilters
                    ? "bg-stone-900 text-white"
                    : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-50"
                }`}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                <span className="hidden sm:inline">フィルター</span>
                {activeFiltersCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-teal-500 text-white text-xs flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>

            {/* Area Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
              <button
                onClick={() => {
                  setArea(undefined);
                  setPage(1);
                }}
                className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  !area
                    ? "bg-stone-900 text-white"
                    : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-50"
                }`}
              >
                すべて
              </button>
              {AREAS.map((a) => (
                <button
                  key={a}
                  onClick={() => {
                    setArea(a);
                    setPage(1);
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    area === a
                      ? "bg-stone-900 text-white"
                      : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-50"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="mt-4 card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-stone-900">絞り込み条件</h3>
                  {activeFiltersCount > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-sm text-stone-500 hover:text-stone-700 flex items-center gap-1"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      クリア
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <FilterCheckbox
                    checked={filters.strollerEntry === true}
                    onChange={() => toggleFilter("strollerEntry")}
                    label="そのまま着席OK"
                  />
                  <FilterCheckbox
                    checked={filters.hasDiaperChanging === true}
                    onChange={() => toggleFilter("hasDiaperChanging")}
                    label="オムツ替えスペース"
                  />
                  <FilterCheckbox
                    checked={filters.hasNursingRoom === true}
                    onChange={() => toggleFilter("hasNursingRoom")}
                    label="授乳室あり"
                  />
                  <FilterCheckbox
                    checked={filters.hasKidsMenu === true}
                    onChange={() => toggleFilter("hasKidsMenu")}
                    label="キッズメニュー"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-sm text-stone-500">
              {isLoading ? (
                "検索中..."
              ) : (
                <>
                  <span className="text-stone-900 font-semibold">{data?.total || 0}</span>
                  件の店舗
                </>
              )}
            </p>
          </div>

          {/* Results Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/5] bg-stone-200/50 rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : data?.items.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-stone-200/50 flex items-center justify-center mx-auto mb-6">
                <svg className="h-8 w-8 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-2">
                店舗が見つかりませんでした
              </h3>
              <p className="text-stone-500 mb-6">検索条件を変更してお試しください</p>
              <button onClick={clearAllFilters} className="btn-primary">
                検索条件をリセット
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data?.items.map((store: any) => (
                  <StoreCard key={store.id} store={store} />
                ))}
              </div>

              {/* Pagination */}
              {data && data.totalPages > 1 && (
                <div className="mt-12 flex justify-center items-center gap-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-ghost disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    前へ
                  </button>
                  <span className="text-sm text-stone-600">
                    {page} / {data.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                    disabled={page === data.totalPages}
                    className="btn-ghost disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    次へ
                    <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function FilterCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center space-x-3 p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
      />
      <span className="text-sm font-medium text-stone-700">{label}</span>
    </label>
  );
}
